import { useCallback, useMemo } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ArrowRight, HelpCircle, Link2, MessageCircle, Search } from "lucide-react";

import { PageHero, Section } from "@/components/layout/Page";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useI18n } from "@/context/I18nContext";
import { getLocalizedMeta } from "@/lib/utils/seo";
import { faqHubHeadScript } from "@/lib/utils/faq-schema";
import { FAQ_CATEGORIES, FAQS, type FaqCategoryKey, type FaqEntry } from "@/lib/mock/faqs";

/**
 * Filter state lives in the URL so a filtered view can be linked, bookmarked
 * and crawled — the same contract /glossary uses. A bare /faqs is the whole set.
 */
const faqSearchSchema = z.object({
  q: z.string().trim().max(80).optional(),
  cat: z
    .enum(["ordering", "logistics", "quality", "hatchery", "products", "export", "company"])
    .optional(),
});

export const Route = createFileRoute("/faqs")({
  validateSearch: (search) => faqSearchSchema.parse(search),
  head: () => ({
    meta: getLocalizedMeta("faq"),
    // The hub publishes every entry, so its FAQPage markup covers all of them.
    scripts: [faqHubHeadScript()],
  }),
  component: FAQs,
});

function FAQs() {
  const { t } = useI18n();
  const { q, cat } = Route.useSearch();
  const navigate = useNavigate({ from: "/faqs" });

  const query = q ?? "";
  const activeCat: FaqCategoryKey | "all" = cat ?? "all";

  // `replace` so typing does not fill the back button with one entry per
  // keystroke; the shareable URL is the point, not the history trail.
  const setQuery = (next: string) =>
    navigate({ search: (s) => ({ ...s, q: next || undefined }), replace: true });

  const setActiveCat = (next: FaqCategoryKey | "all") =>
    navigate({ search: (s) => ({ ...s, cat: next === "all" ? undefined : next }), replace: true });

  // Locale overrides fall back to the English source, as product and glossary
  // copy do.
  const tf = useCallback(
    (entry: FaqEntry, field: "q" | "a"): string => {
      const key = `faq.${entry.slug}.${field}`;
      const res = t(key);
      return res === key ? entry[field] : res;
    },
    [t],
  );

  const tcat = (key: FaqCategoryKey, fallback: string): string => {
    const res = t(`faq.category.${key}`);
    return res === `faq.category.${key}` ? fallback : res;
  };

  // Search covers the answer too: buyers arrive with the situation ("my eggs
  // are stuck at customs"), rarely with our wording for it.
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return FAQS.filter((e) => {
      if (activeCat !== "all" && e.category !== activeCat) return false;
      if (!needle) return true;
      return (
        tf(e, "q").toLowerCase().includes(needle) ||
        tf(e, "a").toLowerCase().includes(needle) ||
        e.q.toLowerCase().includes(needle) ||
        e.a.toLowerCase().includes(needle)
      );
    });
  }, [query, activeCat, tf]);

  const visibleCategories = FAQ_CATEGORIES.filter((c) => matches.some((e) => e.category === c.key));

  return (
    <>
      <PageHero
        eyebrow={t("faqsPage.hero.eyebrow")}
        title={t("faqsPage.hero.title")}
        description={t("faqsPage.hero.description")}
      />

      {/* Sticky filter bar: sixty answers stop being navigable as a flat list,
          and the subject a visitor came with is the fastest way to cut them
          down — the same pattern the glossary uses for its 88 terms.
          Offset by the site header's own sticky height (64 px, plus the 33 px
          notice bar from lg up) — at top-0 it parks *behind* the header, which
          hides the chips and swallows clicks aimed at them. */}
      <div className="sticky top-[var(--header-h)] z-30 border-b border-border/80 bg-background/95 backdrop-blur-md">
        <div className="mx-auto max-w-7xl space-y-3 px-4 py-4 md:px-6">
          <div className="relative max-w-xl">
            <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("faqsPage.searchPlaceholder")}
              aria-label={t("faqsPage.searchPlaceholder")}
              className="h-11 bg-background ps-10 text-sm"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <CatChip
              active={activeCat === "all"}
              onClick={() => setActiveCat("all")}
              label={`${t("faqsPage.filter.all")} (${FAQS.length})`}
            />
            {FAQ_CATEGORIES.map((c) => (
              <CatChip
                key={c.key}
                active={activeCat === c.key}
                onClick={() => setActiveCat(c.key)}
                label={tcat(c.key, c.label)}
              />
            ))}
          </div>
        </div>
      </div>

      <Section>
        {matches.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("faqsPage.noResults")} <span className="font-bold text-navy">“{query}”</span>.
          </p>
        ) : (
          <div className="space-y-14">
            {visibleCategories.map((category) => {
              const entries = matches.filter((e) => e.category === category.key);
              return (
                <div key={category.key} id={`cat-${category.key}`} className="scroll-mt-28">
                  <div className="mb-6 flex items-center gap-3 border-b border-border/80 pb-3">
                    <HelpCircle className="h-5 w-5 shrink-0 text-primary" />
                    <h2 className="font-display text-xl font-bold text-navy md:text-2xl">
                      {tcat(category.key, category.label)}
                    </h2>
                    <span className="ms-auto text-xs font-bold text-muted-foreground">
                      {entries.length}
                    </span>
                  </div>

                  <Accordion type="single" collapsible className="w-full space-y-3">
                    {entries.map((entry) => (
                      <AccordionItem
                        key={entry.slug}
                        value={entry.slug}
                        id={entry.slug}
                        className="card-flat scroll-mt-28 rounded-xl px-5"
                      >
                        <AccordionTrigger className="group py-4 text-start font-display text-base font-semibold text-navy hover:no-underline">
                          <span>
                            {tf(entry, "q")}
                            <Link2 className="ms-1.5 inline h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                          </span>
                        </AccordionTrigger>
                        {/* forceMount keeps every answer in the served HTML —
                            an answer a crawler cannot read is an answer that
                            does not count, and the FAQPage markup above claims
                            all of them are on this page. */}
                        <AccordionContent
                          forceMount
                          className="pb-4 text-sm leading-relaxed text-muted-foreground"
                        >
                          {tf(entry, "a")}
                          {entry.link && (
                            <Link
                              to={entry.link.to}
                              className="mt-2.5 inline-flex items-center text-xs font-bold text-primary hover:text-navy"
                            >
                              {(() => {
                                const key = `faq.${entry.slug}.link`;
                                const res = t(key);
                                return res === key ? entry.link.label : res;
                              })()}
                              <ArrowRight className="ms-1 h-3 w-3" />
                            </Link>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Section band>
        <div className="mx-auto max-w-2xl space-y-4 text-center">
          <h2 className="font-display text-2xl font-bold text-navy md:text-3xl">
            {t("faqsPage.cta.title")}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t("faqsPage.cta.description")}
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <Button asChild size="lg" className="rounded-xl font-bold">
              <Link to="/contactus">
                <MessageCircle className="me-2 h-4 w-4" /> {t("faqsPage.cta.contact")}
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-bold">
              <Link to="/request-quote">
                {t("faqsPage.cta.quote")} <ArrowRight className="ms-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}

function CatChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-xl border px-3.5 py-2 text-xs font-bold transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border/80 bg-background text-navy hover:border-primary/50 hover:bg-surface-alt"
      }`}
    >
      {label}
    </button>
  );
}
